'use client'

/* eslint-disable @next/next/no-img-element */
import { composeTheme } from '@css-modules-theme/core'
import Image from 'next/image'
import { FunctionComponent, ReactNode, useEffect } from 'react'
import { segment } from '@/utils/tracking.utils'
import StringToReactNode from '@/app/_components/StringToReactNode/StringToReactNode'
import { SegmentProductViewed } from '@/types/tracking.types'
import { EventMeta, getEventMeta, hasMeta } from '@/utils/events.utils'
import { getPromoCodeList } from '@/utils/cart.utils'
import { EVENT_HEADER_IMAGE_HEIGHT, EVENT_HEADER_IMAGE_WIDTH } from '@/config/layout.config'
import { getEventImages } from '@/utils/transformers.utils'

// styles
import sharedEventStyles from '@/styles/templates/Events.module.css'
import eventStyles from './EventHeader.module.css'
import { useCart } from '@/app/_providers/CartProvider/CartProvider'
import { useEvent } from '@/app/_providers/EventProvider/EventProvider'
import TicketsBlock from '@/app/_components/EventHeader/TicketsBlock'
import DateAndTime from '@/app/_components/EventHeader/DateAndTime'
import ExternalUrlActionButton from '@/app/_components/EventHeader/ExternalUrlActionButton'
import SupportingActs from '@/app/_components/EventHeader/SupportingActs'
import Banner from '@/app/_components/EventHeader/Banner'
import TXT from '@/messages'

type Props = {
  meta: EventMeta
  eventOnSale: boolean
}

const EventHeader: FunctionComponent<Props> = ({
  meta,
  eventOnSale,
}: Props): ReactNode => {
  const event = useEvent<Transformed.EventWithTimeslotsAndMeta>()
  const styles = composeTheme([
    { theme: sharedEventStyles },
    { theme: eventStyles },
  ])
  const { cart } = useCart()

  const externalUrl = hasMeta('externalTicketUrl', meta)
    && meta.externalTicketUrl

  useEffect(() => {
    // track if external
    if (event && externalUrl) {
      const eventMeta = event.meta ? getEventMeta(event.meta) : {} as EventMeta
      const payload: SegmentProductViewed = {
        name: event.title,
        category: event.category.toString(),
        // eslint-disable-next-line camelcase
        product_id: event.id,
        sku: event.id,
        brand: '',
        variant: 'external sale',
        coupon: cart
          ? getPromoCodeList(cart)
            .join(', ')
          : '',
        currency: 'USD',
        price: 0,
        quantity: 0,
        position: 0,
        value: 0,
        url: event.slug,
        // eslint-disable-next-line camelcase
        image_url: eventMeta.imageProfile || '',
      }
      segment.productViewed(payload)
    }
  }, [cart, event, externalUrl])

  if (!event || !event.meta || !event.ticketCategories || !event.timeslots) {
    return null
  }

  const images = getEventImages(event.meta)

  return (
    <div>
      <header className={styles.EventHeader}>
        <div className={styles.DetailsContainer} data-testid="event-details-card">
          <div className={styles.ImageContainer}>
            {images.eventHeader && (
              <Image
                width={EVENT_HEADER_IMAGE_WIDTH}
                height={EVENT_HEADER_IMAGE_HEIGHT}
                src={images.eventHeader}
                alt={event.title}
              />
            )}
            <Banner />
          </div>
          <section className={styles.EventDetailsHeaderContent}>
            <h1 className={styles.Title} id="event-title" data-testid="event-header-title">
              {event.title}
            </h1>
            {event.subtitle && (
              <StringToReactNode
                tagName="h2"
                incomingString={event.subtitle}
                className={styles.EventSubtitle}
                data-testid="event-subtitle"
              />
            )}
            <DateAndTime />
            <SupportingActs meta={meta} />
            <hr className={styles.Separator} />
            <ExternalUrlActionButton eventOnSale={eventOnSale} meta={meta} />
            <TicketsBlock />
          </section>
        </div>
      </header>
      <div className={styles.SwoopBottom}>
        <img
          src="/static/events/svg/event-details-bottom.svg"
          alt={TXT.eventsA11y.heroDivider}
          tabIndex={-1}
          aria-hidden="true"
        />
      </div>
    </div>
  )
}

export default EventHeader
